import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { clearLegacySessionToken, verifySession } from "@/lib/session";

const SessionContext = createContext(null);

export function createLatestRequestGuard() {
  let latestRequest = 0;
  return {
    begin() {
      latestRequest += 1;
      return latestRequest;
    },
    isCurrent(requestId) {
      return requestId === latestRequest;
    },
  };
}

export function SessionProvider({ children }) {
  const [status, setStatus] = useState("checking");
  const requestGuard = useRef(null);
  if (!requestGuard.current) requestGuard.current = createLatestRequestGuard();

  const refreshSession = useCallback(async () => {
    const requestId = requestGuard.current.begin();
    setStatus("checking");
    try {
      const ok = await verifySession();
      clearLegacySessionToken();
      if (requestGuard.current.isCurrent(requestId)) {
        setStatus(ok ? "authenticated" : "unauthenticated");
      }
      return ok;
    } catch {
      clearLegacySessionToken();
      if (requestGuard.current.isCurrent(requestId)) {
        setStatus("unauthenticated");
      }
      return false;
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const value = useMemo(
    () => ({
      status,
      isAuthenticated: status === "authenticated",
      isChecking: status === "checking",
      refreshSession,
    }),
    [status, refreshSession]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return context;
}
